// agentTypes.ts — types + API calls for the Agents page.
// Agents are records the admin keeps (brokers, channel partners, field agents);
// they never log in. Mirrors models/agent.go.

import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

export interface Agent {
  id: string;
  agency_id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  profession?: string;
  city?: string;
  address?: string;
  /** percentage, 0–100 */
  commission?: number;
  notes?: string;
  /** set when the agent was made by transferring a lead */
  from_lead?: { lead_id: string; sheet_id?: string; source?: string; transferred_at: string };
  is_active: boolean;
  /** leads this agent brought in, and how many of them converted (list only) */
  lead_count: number;
  converted_count: number;
  /** the lead this agent came from is still kept, so they can be moved back to Leads */
  has_lead_history: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** What the form sends. On update, send only what changed; "" clears an optional text field. */
export interface AgentBody {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
  profession?: string;
  city?: string;
  address?: string;
  /** number, or null to clear */
  commission?: number | null;
  notes?: string;
  is_active?: boolean;
}

interface ListRes {
  success: boolean;
  data: Agent[];
}
interface OneRes {
  success: boolean;
  message?: string;
  data: Agent;
}

export const agentApi = {
  list: (params?: { q?: string; is_active?: boolean }) => apiClient.get<ListRes>('/v1/agents', { params }).then((r) => r.data),
  create: (body: AgentBody) => apiClient.post<OneRes>('/v1/agents', body).then((r) => r.data),
  update: (id: string, body: AgentBody) => apiClient.patch<OneRes>(`/v1/agents/${id}`, body).then((r) => r.data),
  /** discardHistory: also throw away the lead history kept for an agent who came from a lead. */
  remove: (id: string, discardHistory = false) => apiClient.delete(`/v1/agents/${id}`, { params: discardHistory ? { discard_history: true } : undefined }).then((r) => r.data),
  /** Admin: turn a lead into an agent record and remove the lead. */
  /** Admin: move an agent back to Leads, with the lead's full history. Returns the lead's id. */
  transferToLead: (agentId: string) => apiClient.post<{ success: boolean; data: { lead_id: string } }>(`/v1/agents/${agentId}/transfer-to-lead`).then((r) => r.data),
  transferLead: (leadId: string) => apiClient.post<OneRes>(`/v1/leads/${leadId}/transfer-to-agent`).then((r) => r.data),
};

export const AGENT_LIMITS = { name: 80, company: 100, profession: 80, city: 80, address: 250, email: 120, notes: 1000 } as const;

/** "9876543210" → "98765 43210" for display; anything else is shown as stored. */
export const fmtPhone = (p: string): string => (/^\d{10}$/.test(p) ? `${p.slice(0, 5)} ${p.slice(5)}` : p);

/** wa.me wants digits only; a bare 10-digit number is treated as Indian. */
export const waLink = (p: string): string => {
  const d = p.replace(/\D/g, '');
  return `https://wa.me/${d.length === 10 ? `91${d}` : d}`;
};